// stores/useGlobalStore.ts
import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import axiosInstance from '@/lib/api/core/axiosInstance';

// ============================================================================
// Types
// ============================================================================

interface User {
  userId: string;
  username: string;
  clientId: string;
  level?: string;
  expiredDate?: string;
  role?: string;
}

interface Client {
  clientId: string;
  clientName: string;
  businessNumber?: string;
  address?: string;
  contact?: string;
  email?: string;
  status?: string;
  createdAt?: string;
}

interface SiteData {
  siteId: string;
  siteName: string;
  location?: string;
  description?: string;
  clientId: string;
  status?: string;
  createdAt?: string;
}

interface MCU {
  mcuId: string;
  mcuName: string;
  siteId: string;
  status: string;
  ipAddress?: string;
  lastConnected?: string;
  firmwareVersion?: string;
}

interface Sensor {
  sensorId: string;
  deviceId: number;
  deviceName: string;
  deviceType: string;
  gpio: number;
  unit: string;
  status: string;
  mcuId: string;
  minValue?: number;
  maxValue?: number;
}

interface Actuator {
  actuatorId: string;
  deviceId: number;
  deviceName: string;
  deviceType: string;
  gpio: number;
  status: string;
  mcuId: string;
  controlType?: string;
}

// ============================================================================
// Store Interface
// ============================================================================

interface GlobalStore {
  // ========================================
  // State
  // ========================================

  // User & Auth
  user: User | null;
  client: Client | null;
  clientId: string | null;

  // Site & Devices
  selectedSiteId: string | null;
  siteData: SiteData | null;
  mcuList: MCU[];
  sensors: Sensor[];
  actuators: Actuator[];

  // UI State
  isLoading: boolean;
  error: string | null;

  // ========================================
  // User Actions
  // ========================================

  setUser: (user: User) => void;
  setClient: (client: Client) => void;
  setClientId: (id: string | null) => void;
  clearUser: () => void;

  // ========================================
  // Site Actions
  // ========================================

  setSelectedSite: (siteId: string | null) => void;
  setSiteData: (data: SiteData | null) => void;
  clearSiteData: () => void;

  // ========================================
  // Device Actions
  // ========================================

  setMcuList: (list: MCU[]) => void;
  setSensors: (sensors: Sensor[]) => void;
  setActuators: (actuators: Actuator[]) => void;
  clearDeviceData: () => void;

  // ========================================
  // Complex Actions (Async)
  // ========================================

  loadClientData: (clientId: string) => Promise<void>;
  loadSiteData: (siteId: string) => Promise<void>;
  loadAllSiteDevices: (siteId: string) => Promise<void>;

  // ========================================
  // Computed Properties
  // ========================================

  isAuthenticated: () => boolean;
  hasSelectedSite: () => boolean;
  getActiveMCUs: () => MCU[];
  getActiveSensors: () => Sensor[];
}

// ============================================================================
// Store Implementation
// ============================================================================

export const useGlobalStore = create<GlobalStore>()(
  persist(
    (set, get) => ({
      // ========================================
      // Initial State
      // ========================================

      user: null,
      client: null,
      clientId: null,

      selectedSiteId: null,
      siteData: null,
      mcuList: [],
      sensors: [],
      actuators: [],

      isLoading: false,
      error: null,

      // ========================================
      // User Actions
      // ========================================

      setUser: (user) => {
        console.log('📝 [Store] setUser:', user.userId);
        set({
          user,
          clientId: user.clientId,
        });
      },

      setClient: (client) => {
        console.log('📝 [Store] setClient:', client.clientId);
        set({
          client,
          clientId: client.clientId,
        });
      },

      setClientId: (id) => {
        console.log('📝 [Store] setClientId:', id);
        set({ clientId: id });
      },

      clearUser: () => {
        console.log('🧹 [Store] clearUser');
        set({
          user: null,
          client: null,
          clientId: null,
          selectedSiteId: null,
          siteData: null,
          mcuList: [],
          sensors: [],
          actuators: [],
          error: null,
        });
      },

      // ========================================
      // Site Actions
      // ========================================

      setSelectedSite: (siteId) => {
        console.log('📝 [Store] setSelectedSite:', siteId);
        set({ selectedSiteId: siteId });
      },

      setSiteData: (data) => {
        console.log('📝 [Store] setSiteData:', data?.siteName);
        set({ siteData: data });
      },

      clearSiteData: () => {
        console.log('🧹 [Store] clearSiteData');
        set({
          selectedSiteId: null,
          siteData: null,
          mcuList: [],
          sensors: [],
          actuators: [],
        });
      },

      // ========================================
      // Device Actions
      // ========================================

      setMcuList: (list) => {
        console.log('📝 [Store] setMcuList:', list.length);
        set({ mcuList: list });
      },

      setSensors: (sensors) => {
        console.log('📝 [Store] setSensors:', sensors.length);
        set({ sensors });
      },

      setActuators: (actuators) => {
        console.log('📝 [Store] setActuators:', actuators.length);
        set({ actuators });
      },

      clearDeviceData: () => {
        console.log('🧹 [Store] clearDeviceData');
        set({
          mcuList: [],
          sensors: [],
          actuators: [],
        });
      },

      // ========================================
      // Complex Actions (Async)
      // ========================================

      loadClientData: async (clientId: string) => {
        console.log('📡 [Store] loadClientData:', clientId);
        set({ isLoading: true, error: null });

        try {
          const response = await axiosInstance.get(`/api/clients/${clientId}`);
          const clientData = response.data;

          set({
            client: clientData,
            clientId: clientData.clientId,
            isLoading: false,
          });

          console.log('✅ [Store] Client 데이터 로드 완료:', clientData.clientName);
          return clientData;

        } catch (error: any) {
          const errorMsg = error.response?.data?.message || 'Client 데이터 로드 실패';
          console.error('❌ [Store] loadClientData 에러:', errorMsg);

          set({
            error: errorMsg,
            isLoading: false,
          });

          throw error;
        }
      },

      loadSiteData: async (siteId: string) => {
        const { clientId } = get();
        console.log('📡 [Store] loadSiteData:', { siteId, clientId });

        if (!clientId) {
          const errorMsg = 'ClientId가 필요합니다';
          console.error('❌ [Store]', errorMsg);
          set({ error: errorMsg });
          throw new Error(errorMsg);
        }

        set({ isLoading: true, error: null });

        try {
          const response = await axiosInstance.get(`/api/sites/${siteId}`);
          const siteData = response.data;

          set({
            siteData,
            selectedSiteId: siteId,
            isLoading: false,
          });

          console.log('✅ [Store] Site 데이터 로드 완료:', siteData.siteName);
          return siteData;

        } catch (error: any) {
          const errorMsg = error.response?.data?.message || 'Site 데이터 로드 실패';
          console.error('❌ [Store] loadSiteData 에러:', errorMsg);

          set({
            error: errorMsg,
            isLoading: false,
          });

          throw error;
        }
      },

      loadAllSiteDevices: async (siteId: string) => {
        console.log('📡 [Store] loadAllSiteDevices:', siteId);
        set({ isLoading: true, error: null });

        try {
          // 병렬로 모든 디바이스 데이터 로드
          const [mcuRes, sensorsRes, actuatorsRes] = await Promise.all([
            axiosInstance.get(`/api/sites/${siteId}/mcus`),
            axiosInstance.get(`/api/sites/${siteId}/sensors`),
            axiosInstance.get(`/api/sites/${siteId}/actuators`),
          ]);

          set({
            mcuList: mcuRes.data,
            sensors: sensorsRes.data,
            actuators: actuatorsRes.data,
            isLoading: false,
          });

          console.log('✅ [Store] 디바이스 데이터 로드 완료:', {
            mcus: mcuRes.data.length,
            sensors: sensorsRes.data.length,
            actuators: actuatorsRes.data.length,
          });

        } catch (error: any) {
          const errorMsg = error.response?.data?.message || '디바이스 데이터 로드 실패';
          console.error('❌ [Store] loadAllSiteDevices 에러:', errorMsg);

          set({
            error: errorMsg,
            isLoading: false,
          });

          throw error;
        }
      },

      // ========================================
      // Computed Properties
      // ========================================

      isAuthenticated: () => {
        const { user, clientId } = get();
        return !!user && !!clientId;
      },

      hasSelectedSite: () => {
        const { selectedSiteId } = get();
        return !!selectedSiteId;
      },

      getActiveMCUs: () => {
        const { mcuList } = get();
        return mcuList.filter((mcu) => mcu.status === 'ACTIVE');
      },

      getActiveSensors: () => {
        const { sensors } = get();
        return sensors.filter((sensor) => sensor.status === 'ACTIVE');
      },
    }),
    {
      name: 'ifms-global-storage',
      storage: createJSONStorage(() => localStorage),

      // persist할 필드만 선택
      partialize: (state) => ({
        user: state.user,
        client: state.client,
        clientId: state.clientId,
        selectedSiteId: state.selectedSiteId,
      }),

      // 옵션: hydration 완료 후 실행
      onRehydrateStorage: () => (state) => {
        if (state) {
          console.log('💧 [Store] Hydration 완료:', {
            hasUser: !!state.user,
            hasClient: !!state.client,
            clientId: state.clientId,
          });
        }
      },
    }
  )
);

// ============================================================================
// Selector Hooks (옵션)
// ============================================================================

// 개별 selector를 만들어 불필요한 리렌더링 방지
export const useUser = () => useGlobalStore((state) => state.user);
export const useClient = () => useGlobalStore((state) => state.client);
export const useClientId = () => useGlobalStore((state) => state.clientId);
export const useSiteData = () => useGlobalStore((state) => state.siteData);
export const useMcuList = () => useGlobalStore((state) => state.mcuList);
export const useSensors = () => useGlobalStore((state) => state.sensors);
export const useActuators = () => useGlobalStore((state) => state.actuators);
export const useIsLoading = () => useGlobalStore((state) => state.isLoading);
export const useStoreError = () => useGlobalStore((state) => state.error);
